import { createContext, useContext } from 'react';
import { SxProps, Theme, alpha } from '@mui/material';

// Shared by the review's tables so their columns line up.

export const numberFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 0,
});

const oreFormat = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatAmount = (value: number | undefined, ore = false) =>
  value === undefined || Math.round(value * (ore ? 100 : 1)) === 0
    ? '-'
    : (ore ? oreFormat : numberFormat).format(value);

// Whether the review's amounts show öre; whole kronor don't add up to the totals.
export const ShowOreContext = createContext(true);

// Whether a negative amount's minus sign sits at the cell's left edge (Detaljer tab).
export const MinusLeftContext = createContext(false);

export const useFormatAmount = () => {
  const ore = useContext(ShowOreContext);
  return (value: number | undefined) => formatAmount(value, ore);
};

// The first three columns stay put while the period columns scroll horizontally.
export const STICKY_COLUMNS = [
  { width: 80, left: 0 },
  { width: 340, left: 80 },
  { width: 140, left: 420 },
];

export const stickySx = (index: number, header = false): SxProps<Theme> => ({
  position: 'sticky',
  left: STICKY_COLUMNS[index].left,
  minWidth: STICKY_COLUMNS[index].width,
  maxWidth: STICKY_COLUMNS[index].width,
  zIndex: header ? 3 : 1,
  bgcolor: 'background.paper',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// Styling follows the Excel mockup: 11px body text, 12px bold period headers, and the
// value cells in the mockup's pale yellow (a translucent tint of it in dark mode).
const MOCKUP_YELLOW = '#ffffcc';
// #ffffcc itself washes out to grey over the dark theme; a warmer amber keeps it yellow.
const DARK_YELLOW = '#ffd54f';
const MOCKUP_GRID = '#b2acbe';

export const tableSx: SxProps<Theme> = ({ palette }) => ({
  '& .MuiTableCell-root': {
    fontSize: 11,
    lineHeight: '16px',
    py: '1px',
    px: '6px',
    borderColor: palette.mode === 'dark' ? palette.divider : MOCKUP_GRID,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontSize: 12,
    fontWeight: 'bold',
    py: '4px',
  },
});

export const valueCellSx: SxProps<Theme> = ({ palette }) => ({
  bgcolor:
    palette.mode === 'dark' ? alpha(DARK_YELLOW, 0.16) : MOCKUP_YELLOW,
  borderLeft: '1px solid',
  borderLeftColor: palette.mode === 'dark' ? palette.divider : MOCKUP_GRID,
  whiteSpace: 'nowrap',
});

// Dotted line above each account group, as in the mockup.
export const accountRowSx: SxProps<Theme> = ({ palette }) => ({
  '& .MuiTableCell-root': {
    borderTop: '1px dotted',
    borderTopColor: palette.text.secondary,
  },
});

export const fteFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 1,
});
